import React from 'react';
import { stripTrailingSlash } from '@zapier/url-utils';

import {
  CurrentVersionReadOnlyReason,
  useCurrentVersionReadOnlyReason,
} from 'app/developer-v3/hooks';

import ByCliToolMessage from '../ByCliToolMessage';
import Menu from './Menu';
import VersionPicker from './VersionPicker';
import { SIDEBAR_ITEMS } from './constants';
import { VersionLifecycle } from 'app/developer-v3/types/statuses';

import type { PartialDefinition } from 'app/developer-v3/types';
import type { SemVer } from 'app/developer-v3/types/version';

type Props = {
  baseUrl: string;
  currentUrl: string;
  definition: PartialDefinition;
  definitionOverride: PartialDefinition;
  isGUIIntegration: boolean;
  onVersionChange: (versionNumber: string) => {};
  usersExceedLimit: boolean;
  version: SemVer;
  versions: SemVer[];
  versionLifecycle: VersionLifecycle;
};

const SectionBuild = ({
  baseUrl,
  currentUrl,
  definition,
  definitionOverride,
  isGUIIntegration,
  onVersionChange,
  usersExceedLimit,
  version,
  versions,
  versionLifecycle,
}: Props) => {
  const currentUrlWithoutTrailingSlash = stripTrailingSlash(currentUrl);
  const currentUrlWithTrailingSlash = `${currentUrlWithoutTrailingSlash}/`;
  const currentVersionReadOnlyReason = useCurrentVersionReadOnlyReason();
  const isNotAdmin =
    currentVersionReadOnlyReason === CurrentVersionReadOnlyReason.NOT_ADMIN;

  const itemPropsArray = SIDEBAR_ITEMS.BUILD.map(({ key, name, section }) => {
    const itemProps = Object.create({});

    switch (key) {
      case 'version-overview': {
        const url = baseUrl;
        const isActive = currentUrlWithoutTrailingSlash === url;
        const item = { isActive, isEnabled: true, label: name, section, url };

        Object.assign(itemProps, { key, item });
        break;
      }
      case 'authentication': {
        const url = `${baseUrl}/${key}`;
        const isActive = currentUrlWithoutTrailingSlash.startsWith(url);
        const item = {
          isActive,
          isEnabled: isGUIIntegration,
          label: name,
          section,
          url,
        };

        Object.assign(itemProps, { key, item });
        break;
      }
      case 'triggers': {
        const { triggers = {} } = { ...definition, ...definitionOverride };

        const url = `${baseUrl}/${key}`;
        const isActive = currentUrlWithoutTrailingSlash.startsWith(url);
        const item = {
          isActive,
          isEnabled: true,
          label: `${name} (${Object.keys(triggers).length})`,
          section,
          url,
        };

        Object.assign(itemProps, { key, item });
        break;
      }
      case 'actions': {
        const { creates = {}, searches = {} } = {
          ...definition,
          ...definitionOverride,
        };

        const url = `${baseUrl}/${key}`;
        const isActive = currentUrlWithoutTrailingSlash.startsWith(url);
        const item = {
          isActive,
          isEnabled: true,
          label: `${name} (${
            Object.keys(creates).length + Object.keys(searches).length
          })`,
          section,
          url,
        };

        Object.assign(itemProps, { key, item });
        break;
      }
      case 'advanced': {
        const url = `${baseUrl}/${key}`;
        const isActive = currentUrlWithoutTrailingSlash.startsWith(url);
        const isAdmin = !isNotAdmin;
        const item = {
          isActive,
          isEnabled: isAdmin,
          label: name,
          section,
          tooltip: isNotAdmin ? 'Admin access only' : undefined,
          url,
        };

        Object.assign(itemProps, { key, item });
        break;
      }
      default: {
        const url = `${baseUrl}/${key}`;
        const isActive = currentUrlWithTrailingSlash.startsWith(`${url}/`);
        const item = {
          isActive,
          isEnabled: isGUIIntegration,
          label: name,
          section,
          url,
        };
        Object.assign(itemProps, { key, item });
      }
    }

    return itemProps;
  });

  return (
    <Menu
      disabledMessage={
        isNotAdmin ? 'Admin access only' : <ByCliToolMessage theme="dark" />
      }
      hasTopBorder={true}
      header={
        <VersionPicker
          onVersionChange={onVersionChange}
          usersExceedLimit={usersExceedLimit}
          version={version}
          versionLifecycle={versionLifecycle}
          versions={versions}
        />
      }
      itemsProps={itemPropsArray}
      menuKey="build"
      title="Build"
      titleIconName="actionBuild"
    />
  );
};

export default SectionBuild;
